import React, { useEffect, useState } from "react"
import { Col, ControlLabel, FormGroup, FormControl, HelpBlock } from "react-bootstrap"
import { propEq } from "ramda"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons"
import styled from "@emotion/styled"
import Select from "react-select"
import { Checkbox } from "js/includes/components/NinjaReactICheck"
import {
  localized,
  maxNameLength,
  maxCommandLineParameterLength,
  maxURLPathLength,
  isNinjaUploadsUrl,
} from "js/includes/common/utils"
import ArchitectureSelect from "js/includes/components/scripting/ArchitectureSelect"
import InstallApplicationFileUpload from "./InstallApplicationFileUpload"
import { getCredentialReactSelectOptions } from "../common/credentials"
import { useMounted } from "js/includes/common/hooks"
import { colors } from "js/includes/common/theme"

const WarningIcon = styled(FontAwesomeIcon)`
  color: ${colors.ninjaYellow};
  margin-right: 5px;
`

export default function InstallApplication({
  onChange,
  updateMsiUrlPath,
  updateFileName,
  validateForm,
  validation,
  values: { appName, cmdLineParameters, msiUrlPath, fileName, runAs, supportedArchitecture },
}) {
  const [checked, setChecked] = useState(false)
  const [credentialOptions, setCredentialOptions] = useState([])
  const mounted = useMounted()

  useEffect(() => {
    const setCredentials = async () => {
      const credentials = await getCredentialReactSelectOptions("Windows")
      mounted.current && setCredentialOptions(credentials)
    }

    setCredentials()
  }, [mounted])

  const handleCheckChange = e => {
    const newCheckedValue = !checked

    updateMsiUrlPath("")
    updateFileName("")

    setChecked(newCheckedValue)
  }

  const onUploadSuccess = (filePath, fileName) => {
    updateMsiUrlPath(filePath)
    updateFileName(fileName)
  }

  const onUploadFailure = () => {
    updateMsiUrlPath("")
    updateFileName("")
  }

  const onUploadFileChange = () => {
    updateMsiUrlPath("")
    updateFileName("")
  }

  return (
    <>
      <FormGroup
        className="m-sm m-b-lg text-align-left"
        validationState={validation.success.supportedArchitecture === false ? "error" : null}
      >
        <Col xs={4} componentClass={ControlLabel}>
          {localized("editors.policy.actions.architecture")}
        </Col>
        <Col xs={8}>
          <ArchitectureSelect {...{ supportedArchitecture, onChange }} />
          <HelpBlock className="invalid position-absolute m-t-none">
            {validation.success.supportedArchitecture === false && validation.message.supportedArchitecture}
          </HelpBlock>
        </Col>
      </FormGroup>
      <FormGroup className="m-sm m-b-lg text-align-left">
        <Col xs={4} componentClass={ControlLabel}>
          {localized("editors.policy.actions.uploadFile")}
        </Col>
        <Col xs={8}>
          <div className="install-fileupload-container">
            <div className="m-r-sm">
              <Checkbox
                checkboxClass="icheckbox_square-blue"
                increaseArea="20%"
                checked={checked}
                onChange={handleCheckChange}
              />
            </div>
            <div className="install-fileupload-wrapper">
              {checked && (
                <InstallApplicationFileUpload
                  onUploadSuccess={onUploadSuccess}
                  onUploadFailure={onUploadFailure}
                  onUploadFileChange={onUploadFileChange}
                />
              )}
            </div>
          </div>
          {checked && (
            <>
              <div className="m-t-sm">
                <div>{localized("general.maximumFileSize")}: 1 GiB</div>
              </div>
              <div className="m-t-sm">
                <WarningIcon icon={faExclamationTriangle} />
                {localized("general.doNotUploadSensitiveFiles")}
              </div>
            </>
          )}
        </Col>
      </FormGroup>
      <FormGroup
        className="m-sm m-b-lg text-align-left"
        validationState={validation.success.appName === false ? "error" : null}
      >
        <Col xs={4} componentClass={ControlLabel}>
          {localized("general.name")}
        </Col>
        <Col xs={8}>
          <FormControl
            {...{
              maxLength: maxNameLength,
              onBlur: () => validateForm(["appName"]),
              onChange: e => onChange("appName", e.target.value),
              placeholder: localized("editors.policy.actions.applicationName"),
              value: appName || "",
            }}
          />
          <HelpBlock className="invalid position-absolute m-t-none">
            {validation.success.appName === false && validation.message.appName}
          </HelpBlock>
        </Col>
      </FormGroup>
      {!checked && !isNinjaUploadsUrl(msiUrlPath) && (
        <FormGroup
          className="m-sm m-b-lg text-align-left"
          validationState={validation.success.msiUrlPath === false ? "error" : null}
        >
          <Col xs={4} componentClass={ControlLabel}>
            {localized("editors.policy.actions.msiURL")}
          </Col>
          <Col xs={8}>
            <FormControl
              {...{
                maxLength: maxURLPathLength,
                onBlur: () => validateForm(["msiUrlPath"]),
                onChange(e) {
                  updateMsiUrlPath(e.target.value)
                },
                placeholder: localized("editors.policy.actions.urlOrMsiOrExe"),
                value: msiUrlPath || "",
              }}
            />
            <HelpBlock className="invalid position-absolute m-t-none">
              {validation.success.msiUrlPath === false && validation.message.msiUrlPath}
            </HelpBlock>
          </Col>
        </FormGroup>
      )}
      {(checked || fileName) && (
        <FormGroup className="m-sm m-b-lg text-align-left">
          <Col xs={4} componentClass={ControlLabel}>
            {localized("deviceDashboard.fileName")}
          </Col>
          <Col xs={8} className="m-t-sm">
            {fileName || <span className="text-danger">{localized("general.pending")}</span>}
          </Col>
        </FormGroup>
      )}
      <FormGroup
        {...{
          className: "m-sm m-b-lg text-align-left",
          name: "cmdLineParameters",
        }}
      >
        <Col xs={4} componentClass={ControlLabel}>
          {localized("editors.policy.actions.parameters")}
        </Col>
        <Col xs={8}>
          <FormControl
            {...{
              maxLength: maxCommandLineParameterLength,
              onChange: e => onChange("cmdLineParameters", e.target.value),
              placeholder: localized("editors.policy.actions.commandLineParameters"),
              value: cmdLineParameters || "",
            }}
          />
        </Col>
      </FormGroup>
      <FormGroup
        className="m-sm m-b-lg text-align-left"
        validationState={validation.success.runAs === false ? "error" : null}
      >
        <Col xs={4} componentClass={ControlLabel}>
          {localized("editors.policy.actions.runAs")}
        </Col>
        <Col xs={8}>
          <Select
            id="runAs"
            onChange={selection => onChange("runAs", selection.value)}
            options={credentialOptions}
            value={credentialOptions.find(propEq("value", runAs))}
          />
          <HelpBlock className="invalid position-absolute m-t-none">
            {validation.success.runAs === false && validation.message.runAs}
          </HelpBlock>
        </Col>
      </FormGroup>
    </>
  )
}
